import { useCallback, useEffect, useState, type FormEvent } from "react";

import {
  ApiError,
  apiClient,
  type ImagePilotCandidate,
  type ImagePilotRequest,
  type OperationalModelRoute,
} from "../api/client";

export function ImagePilotPage() {
  const [requests, setRequests] = useState<ImagePilotRequest[]>([]);
  const [candidates, setCandidates] = useState<ImagePilotCandidate[]>([]);
  const [routes, setRoutes] = useState<OperationalModelRoute[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dataSourceId, setDataSourceId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [intendedUse, setIntendedUse] = useState("Amazon 商品详情主图");
  const [brandRules, setBrandRules] = useState("白色背景，不添加未经批准的商标");
  const [rightsBasis, setRightsBasis] = useState("公司自有商品摄影");
  const [prompt, setPrompt] = useState("保持商品真实结构，生成专业白底电商主图");
  const [routeCode, setRouteCode] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async (requestId?: string) => {
    const [nextRequests, nextCandidates, nextRoutes] = await Promise.all([
      apiClient.listImageRequests(),
      apiClient.listImageCandidates(requestId),
      apiClient.listOperationalModelRoutes(),
    ]);
    setRequests(nextRequests);
    setCandidates(nextCandidates);
    const imageRoutes = nextRoutes.filter((route) => route.capability === "image");
    setRoutes(imageRoutes);
    setRouteCode((current) =>
      imageRoutes.some((route) => route.code === current)
        ? current
        : (imageRoutes[0]?.code ?? ""),
    );
    setSelectedId((current) =>
      nextRequests.some((item) => item.id === current)
        ? current
        : (nextRequests[0]?.id ?? ""),
    );
  }, []);

  useEffect(() => {
    refresh().catch((error: unknown) => setMessage(errorMessage(error)));
  }, [refresh]);

  async function upload(event: FormEvent) {
    event.preventDefault();
    if (!file || !dataSourceId.trim() || !siteId.trim()) {
      setMessage("请选择来源图片，并填写数据来源 ID 与站点 ID。");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const created = await apiClient.uploadImageSource(
        {
          data_source_id: dataSourceId.trim(),
          request_key: crypto.randomUUID(),
          site_id: siteId.trim(),
          intended_use: intendedUse.trim(),
          dimensions: { width: 1024, height: 1024, unit: "px" },
          brand_rules: { instruction: brandRules.trim() },
          rights_context: { authorized: true, basis: rightsBasis.trim() },
          prompt_template_version: "product-main-image/v1",
          user_inputs: { brief: prompt.trim() },
        },
        file,
      );
      setSelectedId(created.id);
      await refresh(created.id);
      setMessage("来源图片与权利上下文已登记，可启动固定生成 Workflow。");
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function generate() {
    if (!selectedId || !prompt.trim() || !routeCode) return;
    setBusy(true);
    setMessage("");
    try {
      const run = await apiClient.startImageGeneration(selectedId, {
        prompt: prompt.trim(),
        model: routeCode,
        candidate_count: 2,
        size: "1024x1024",
        region: "US",
        classification: "internal",
        max_cost: "5",
      });
      setMessage(`图片 Workflow ${run.id} 已进入队列；无合规供应商时会明确失败。`);
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function review(
    candidate: ImagePilotCandidate,
    decision: "accepted" | "rejected",
  ) {
    setBusy(true);
    try {
      await apiClient.reviewImageCandidate(candidate.id, decision, "图片试点人工复核");
      await refresh(selectedId);
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack image-pilot-page">
      <header className="flow-header">
        <div>
          <span className="eyebrow">固定 Workflow · 人工选择</span>
          <h1>受控商品图片试点</h1>
          <p>来源权利、供应商路由、成本和候选血缘全程留痕；平台不会自动发布图片。</p>
        </div>
        <span className="phase-badge">{candidates.length} 张候选</span>
      </header>

      <section className="flow-panel image-intake-panel" aria-labelledby="image-source-title">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">来源与权利</span>
            <h2 id="image-source-title">登记来源图片</h2>
          </div>
        </div>
        <form className="image-pilot-form" onSubmit={upload}>
          <label>数据来源 ID<input value={dataSourceId} onChange={(event) => setDataSourceId(event.target.value)} /></label>
          <label>站点 ID<input value={siteId} onChange={(event) => setSiteId(event.target.value)} /></label>
          <label>用途<input value={intendedUse} onChange={(event) => setIntendedUse(event.target.value)} /></label>
          <label>权利依据<input value={rightsBasis} onChange={(event) => setRightsBasis(event.target.value)} /></label>
          <label className="wide-field">品牌规则<textarea value={brandRules} onChange={(event) => setBrandRules(event.target.value)} /></label>
          <label className="wide-field">来源图片<input accept="image/png,image/jpeg,image/webp" type="file" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>
          <button disabled={busy} type="submit">{busy ? "处理中…" : "上传并登记"}</button>
        </form>
      </section>

      <section className="flow-panel" aria-labelledby="image-run-title">
        <div className="panel-heading">
          <div><span className="eyebrow">合规模型网关</span><h2 id="image-run-title">生成候选</h2></div>
          <button className="secondary-button" onClick={() => void refresh(selectedId)} type="button">刷新</button>
        </div>
        <div className="image-generation-controls">
          <label>来源请求<select value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>{requests.map((item) => <option key={item.id} value={item.id}>{item.intended_use} · {item.state}</option>)}</select></label>
          <label>图片逻辑路由<select value={routeCode} onChange={(event) => setRouteCode(event.target.value)}><option value="">请选择已发布路由</option>{routes.map((route) => <option key={route.code} value={route.code}>{route.display_name}</option>)}</select></label>
          <label className="wide-field">生成提示<textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} /></label>
          <button disabled={busy || !selectedId || !routeCode} onClick={() => void generate()} type="button">启动固定 Workflow</button>
        </div>
        {message && <p className="status-banner" role="status">{message}</p>}
      </section>

      <section aria-labelledby="image-candidate-title">
        <div className="section-heading"><div><span className="eyebrow">候选与溯源</span><h2 id="image-candidate-title">人工选择</h2></div></div>
        <div className="image-candidate-grid">
          {candidates.map((candidate) => (
            <article className="image-candidate-card" key={candidate.id}>
              <img alt={`生成候选 ${candidate.candidate_index}`} src={apiClient.imageCandidateUrl(candidate.id)} />
              <div><span className="task-type">{candidate.review_state}</span><h3>候选 {candidate.candidate_index}</h3><p>{candidate.model_name} · ${candidate.estimated_cost}</p></div>
              <details><summary>参数与提示快照</summary><pre>{JSON.stringify({ prompt: candidate.prompt_snapshot, parameters: candidate.generation_parameters }, null, 2)}</pre></details>
              <div className="review-actions"><a className="secondary-button" href={apiClient.imageCandidateUrl(candidate.id)}>下载</a><button disabled={busy} onClick={() => void review(candidate, "accepted")} type="button">接受</button><button className="danger-button" disabled={busy} onClick={() => void review(candidate, "rejected")} type="button">拒绝</button></div>
            </article>
          ))}
          {!candidates.length && <div className="empty-page">暂无候选；完成生成后在此人工复核。</div>}
        </div>
      </section>
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : "图片试点操作失败";
}
